import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import AdminLayout from '../components/AdminLayout';
import { searchAdminCompanies } from '../../../api/adminApi';

const FILTERS = [
  { key: 'all',       label: '전체' },
  { key: 'active',    label: '활성' },
  { key: 'suspended', label: '정지' },
];

export default function AdminCompaniesPage() {
  const navigate = useNavigate();
  const [query,     setQuery]     = useState('');
  const [filter,    setFilter]    = useState('all');
  const [companies, setCompanies] = useState([]);
  const [total,     setTotal]     = useState(0);
  const [page,      setPage]      = useState(1);
  const [loading,   setLoading]   = useState(true);
  const [error,     setError]     = useState(null);

  const LIMIT = 20;

  const fetchCompanies = useCallback(async (q, p) => {
    setLoading(true);
    setError(null);
    try {
      const data = await searchAdminCompanies({ q, page: p, limit: LIMIT });
      // 응답: { total, companies: [...] }
      setCompanies(data.companies ?? []);
      setTotal(data.total ?? 0);
    } catch {
      setError('제작사 목록을 불러오지 못했습니다.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchCompanies(query, page);
  }, [page]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSearch = () => {
    setPage(1);
    fetchCompanies(query, 1);
  };

  // 클라이언트 측 status 필터 (API 필터 파라미터 없으므로 프론트에서 처리)
  const filtered = filter === 'all'
    ? companies
    : companies.filter(c => c.status === filter);

  const totalPages = Math.ceil(total / LIMIT);

  return (
    <AdminLayout pageId="companies">
      <div className="admin-topbar">
        <h1>제작사 목록</h1>
        <div className="admin-topbar__right">
          <span style={{ fontSize: 12, color: 'var(--color-text-tertiary)' }}>
            총 {total.toLocaleString()}개사
          </span>
          <span className="admin-badge"><i className="ti ti-user" /> admin</span>
        </div>
      </div>

      {/* 검색 바 */}
      <div className="ag-card" style={{ marginBottom: 12 }}>
        <div style={{ display: 'flex', gap: 8 }}>
          <input
            className="ag-field__input"
            type="text"
            placeholder="제작사명 또는 이메일"
            value={query}
            onChange={e => setQuery(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && handleSearch()}
            style={{ flex: 1 }}
          />
          <button className="ag-btn ag-btn--primary" style={{ flexShrink: 0 }} onClick={handleSearch}>
            <i className="ti ti-search" /> 검색
          </button>
        </div>
      </div>

      {/* 필터 칩 */}
      <div className="ag-filter-row">
        <span style={{ fontSize: 12, color: 'var(--color-text-tertiary)', flexShrink: 0 }}>필터:</span>
        {FILTERS.map(f => (
          <button
            key={f.key}
            className={`ag-filter-chip${filter === f.key ? ' active' : ''}`}
            onClick={() => setFilter(f.key)}
          >
            {f.label}
          </button>
        ))}
      </div>

      {error && (
        <div style={{ fontSize: 12, color: 'var(--color-danger)', marginBottom: 10 }}>
          <i className="ti ti-alert-triangle" /> {error}
        </div>
      )}

      {/* 결과 수 */}
      <div style={{ fontSize: 12, color: 'var(--color-text-tertiary)', marginBottom: 10 }}>
        {loading ? '불러오는 중...' : `${filtered.length.toLocaleString()}개 제작사`}
      </div>

      {/* 테이블 */}
      <div className="ag-card" style={{ padding: 0, overflow: 'hidden' }}>
        <table className="ag-table">
          <thead>
            <tr>
              {['제작사', '담당자', '이메일', '가입일', '활성 공고', '상태', '관리'].map(h => (
                <th key={h}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={7} className="ag-table__empty">
                  <i className="ti ti-loader" style={{ fontSize: 20, display: 'block', marginBottom: 8 }} />
                  불러오는 중...
                </td>
              </tr>
            ) : filtered.length === 0 ? (
              <tr>
                <td colSpan={7} className="ag-table__empty">
                  <i className="ti ti-building" style={{ fontSize: 20, display: 'block', marginBottom: 8 }} />
                  검색 결과가 없습니다
                </td>
              </tr>
            ) : filtered.map(c => (
              <tr key={c.id}>
                <td>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <div
                      className="ag-avatar"
                      style={{ background: 'var(--color-background-secondary)', color: 'var(--color-text-secondary)' }}
                    >
                      <i className="ti ti-building" style={{ fontSize: 13 }} />
                    </div>
                    <span style={{ fontSize: 13, fontWeight: 500 }}>{c.companyName}</span>
                  </div>
                </td>
                <td style={{ color: 'var(--color-text-secondary)', fontSize: 13 }}>{c.managerName || '—'}</td>
                <td style={{ color: 'var(--color-text-secondary)', fontSize: 12 }}>{c.email}</td>
                <td style={{ color: 'var(--color-text-tertiary)', fontSize: 12 }}>
                  {c.joinedAt?.slice(0, 10)}
                </td>
                <td style={{ color: 'var(--color-text-secondary)', fontSize: 13 }}>
                  {c.activePostings}건
                </td>
                <td>
                  <span className={`ag-badge ${(c.status ?? 'active') === 'active' ? 'ag-badge--success' : 'ag-badge--danger'}`}>
                    {(c.status ?? 'active') === 'active' ? '활성' : '정지'}
                  </span>
                </td>
                <td>
                  <button className="ag-btn ag-btn--sm" onClick={() => navigate(`/admin/companies/${c.id}`)}>
                    <i className="ti ti-edit" /> 수정
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* 페이지네이션 */}
      {totalPages > 1 && (
        <div className="ag-pagination">
          <button className="ag-page-btn" disabled={page === 1} onClick={() => setPage(p => p - 1)}>
            <i className="ti ti-chevron-left" />
          </button>
          {Array.from({ length: Math.min(totalPages, 7) }, (_, i) => {
            const p = totalPages <= 7 ? i + 1 : page <= 4 ? i + 1 : page + i - 3;
            if (p < 1 || p > totalPages) return null;
            return (
              <button
                key={p}
                className={`ag-page-btn${page === p ? ' active' : ''}`}
                onClick={() => setPage(p)}
              >
                {p}
              </button>
            );
          })}
          <button className="ag-page-btn" disabled={page === totalPages} onClick={() => setPage(p => p + 1)}>
            <i className="ti ti-chevron-right" />
          </button>
        </div>
      )}
    </AdminLayout>
  );
}
